import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { compressFilesToWebP } from '@/lib/imageUtils';
import { getErrorMessage } from '@/lib/errors';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { ApiError, BulkCreateAssetInput } from '@pms/shared';
import { bulkCreateAssetSchema } from '@pms/shared';
import {
  assetsService,
  type AssetCategory,
  type AssetStatus,
  type BulkCreateAssetsResult,
} from '@/services/assets.service';
import { FileDropZone } from './FileDropZone';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import { Notice } from '@/components/ui/notice';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const NONE = '__none__';
// From this many copies on, the form warns that a batch of records is about to be created.
const LARGE_BATCH_WARNING = 10;

const DEFAULT_VALUES: BulkCreateAssetInput = {
  category_id: '',
  subcategory_id: null,
  brand_id: null,
  model_name: null,
  purchase_date: null,
  warranty_available: false,
  warranty_end_date: null,
  status_id: '',
  assigned_to_user_id: null,
  assigned_department_id: null,
  notes: null,
  quantity: 1,
};

function successMessage({ count, assets }: BulkCreateAssetsResult): string {
  const first = assets[0]?.asset_code;
  const last = assets[assets.length - 1]?.asset_code;
  return count === 1 ? `Asset ${first} created` : `${count} assets created (${first} – ${last})`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  statuses: AssetStatus[];
  categories: AssetCategory[];
}

export function CreateAssetModal({ open, onClose, statuses, categories }: Props) {
  const queryClient = useQueryClient();
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);

  const { data: brandsData } = useQuery({
    queryKey: ['assets', 'brands'],
    queryFn: () => assetsService.listBrands(),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users', 'active-all'],
    queryFn: () => usersService.listAllActive(),
  });

  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<BulkCreateAssetInput>({
    resolver: zodResolver(bulkCreateAssetSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchCategoryId = watch('category_id');
  const watchWarranty = watch('warranty_available');
  const watchQuantity = watch('quantity');
  const copies = Number.isInteger(watchQuantity) && watchQuantity > 0 ? watchQuantity : 1;

  const { data: subcats = [] } = useQuery({
    queryKey: ['assets', 'subcategories', watchCategoryId],
    queryFn: () => assetsService.listSubcategories(watchCategoryId),
    enabled: !!watchCategoryId,
  });

  // Files go up with the form in one request: the server creates every copy and
  // attaches the files in a single transaction, so a failure leaves nothing behind.
  const mutation = useMutation({
    mutationFn: async (data: BulkCreateAssetInput) => {
      const [invoices, photos] = await Promise.all([
        compressFilesToWebP(invoiceFiles),
        compressFilesToWebP(photoFiles),
      ]);
      return assetsService.bulkCreateAssets(data, { invoices, photos });
    },
    onSuccess: (result) => {
      toast.success(successMessage(result));
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'stats'] });
      reset();
      setInvoiceFiles([]);
      setPhotoFiles([]);
      onClose();
    },
    onError: (e) => {
      const fields = (e as AxiosError<ApiError>).response?.data?.error?.fields ?? {};
      for (const [field, message] of Object.entries(fields)) {
        if (field in DEFAULT_VALUES) setError(field as keyof BulkCreateAssetInput, { message });
      }
      toast.error(getErrorMessage(e));
    },
  });

  const onSubmit = (data: BulkCreateAssetInput) => {
    const cleaned: BulkCreateAssetInput = {
      ...data,
      subcategory_id: data.subcategory_id || null,
      brand_id: data.brand_id || null,
      model_name: data.model_name || null,
      purchase_date: data.purchase_date || null,
      warranty_end_date: data.warranty_available ? data.warranty_end_date : null,
      assigned_to_user_id: data.assigned_to_user_id || null,
      assigned_department_id: data.assigned_department_id || null,
      notes: data.notes || null,
    };
    mutation.mutate(cleaned);
  };

  const handleClose = () => {
    reset();
    setInvoiceFiles([]);
    setPhotoFiles([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Asset</DialogTitle>
        </DialogHeader>

        {/* noValidate: the number input's min/step would otherwise block submit with a
            browser tooltip before the shared Zod schema can show its inline messages. */}
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Category */}
            <div className="space-y-1">
              <Label>Category <span className="text-destructive">*</span></Label>
              <Controller
                control={control}
                name="category_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onValueChange={(v) => {
                      field.onChange(v);
                      // A sub category from the previous category would produce a wrong asset code.
                      setValue('subcategory_id', null);
                    }}
                  >
                    <SelectTrigger className={errors.category_id ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category_id && (
                <p className="text-xs text-destructive">{errors.category_id.message}</p>
              )}
            </div>

            {/* Sub Category */}
            <div className="space-y-1">
              <Label>Sub Category</Label>
              <Controller
                control={control}
                name="subcategory_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                    disabled={subcats.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {subcats.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.subcategory_id && (
                <p className="text-xs text-destructive">{errors.subcategory_id.message}</p>
              )}
            </div>

            {/* Brand */}
            <div className="space-y-1">
              <Label>Brand</Label>
              <Controller
                control={control}
                name="brand_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {(brandsData ?? []).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.brand_id && (
                <p className="text-xs text-destructive">{errors.brand_id.message}</p>
              )}
            </div>

            {/* Model Name */}
            <div className="space-y-1">
              <Label>Model Name</Label>
              <Input
                {...register('model_name')}
                placeholder="e.g. ThinkPad X1 Carbon"
              />
            </div>

            {/* Purchase Date */}
            <div className="space-y-1">
              <Label>Purchase Date</Label>
              <Controller
                control={control}
                name="purchase_date"
                render={({ field }) => (
                  <DatePicker value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                )}
              />
            </div>

            {/* Status */}
            <div className="space-y-1">
              <Label>Status <span className="text-destructive">*</span></Label>
              <Controller
                control={control}
                name="status_id"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.status_id ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.status_id && (
                <p className="text-xs text-destructive">{errors.status_id.message}</p>
              )}
            </div>

            {/* Assigned To */}
            <div className="space-y-1">
              <Label>Assigned To</Label>
              <Controller
                control={control}
                name="assigned_to_user_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Unassigned</SelectItem>
                      {(usersData ?? []).map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.assigned_to_user_id && (
                <p className="text-xs text-destructive">{errors.assigned_to_user_id.message}</p>
              )}
            </div>

            {/* Department */}
            <div className="space-y-1">
              <Label>Department</Label>
              <Controller
                control={control}
                name="assigned_department_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {(deptsData?.data ?? []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.assigned_department_id && (
                <p className="text-xs text-destructive">{errors.assigned_department_id.message}</p>
              )}
            </div>
          </div>

          {/* Warranty toggle — full row */}
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="warranty_available"
              render={({ field }) => (
                <input
                  type="checkbox"
                  id="warranty_available"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                />
              )}
            />
            <Label htmlFor="warranty_available" className="cursor-pointer">
              Warranty Available
            </Label>
          </div>

          {/* Warranty End Date — only when warranty on */}
          {watchWarranty && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Warranty End Date</Label>
                <Controller
                  control={control}
                  name="warranty_end_date"
                  render={({ field }) => (
                    <DatePicker value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                  )}
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              {...register('notes')}
              rows={3}
              placeholder="Additional notes about this asset..."
            />
          </div>

          {/* Number of copies */}
          <div className="space-y-1">
            <Label htmlFor="quantity">Number of copies <span className="text-destructive">*</span></Label>
            <Input
              id="quantity"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              className={`sm:max-w-[12rem] ${errors.quantity ? 'border-destructive' : ''}`}
              {...register('quantity', { valueAsNumber: true })}
            />
            {errors.quantity ? (
              <p className="text-xs text-destructive">{errors.quantity.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Each copy becomes a separate asset with its own code, sharing these details, photos and invoice.
              </p>
            )}
            {copies >= LARGE_BATCH_WARNING && (
              <Notice tone="warning" icon={AlertTriangle} compact className="mt-2">
                This will create {copies} separate asset records.
              </Notice>
            )}
          </div>

          {/* Invoice upload */}
          <div className="space-y-1">
            <Label>Invoice / Bill</Label>
            <FileDropZone
              files={invoiceFiles}
              onFilesChange={setInvoiceFiles}
              accept=".pdf,.jpg,.jpeg,.png"
              placeholder="Click to upload invoice / bill"
            />
          </div>

          {/* Photos upload */}
          <div className="space-y-1">
            <Label>Asset Photos</Label>
            <FileDropZone
              files={photoFiles}
              onFilesChange={setPhotoFiles}
              accept="image/*"
              multiple
              placeholder="Click to upload photos (multiple allowed)"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting || mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {(isSubmitting || mutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {copies === 1 ? 'Create Asset' : `Create ${copies} Assets`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
